import type { FabMode, FabPosition } from "./types";

// Pure geometry behind the launcher's chat-head drag (fab.ts wires it to the
// DOM). Every mode snaps back to the edge it was designed for once the
// visitor lets go: the bubble and the vertical tab glide to the nearest side
// edge and keep their height, the horizontal tab drops back onto the bottom
// edge and keeps its horizontal spot.

export interface Box {
	readonly height: number;
	readonly left: number;
	readonly top: number;
	readonly width: number;
}

export interface Viewport {
	readonly height: number;
	readonly width: number;
}

export interface RestingPlace {
	readonly left: number;
	// The half of the viewport the launcher rests in. The panel docks to this
	// edge so it always slides out next to the launcher.
	readonly side: FabPosition;
	readonly top: number;
}

/**
 * What the snippet remembers between page loads of one tab. `offset` is the
 * free coordinate of the mode: the top for side-snapping modes, the left for
 * the bottom-snapping horizontal tab.
 */
export interface StoredFabPosition {
	readonly mode: FabMode;
	readonly offset: number;
	readonly side: FabPosition;
}

// Keeps a released launcher clear of the viewport edge it does not snap to.
export const VIEWPORT_GUTTER_PX = 8;

export const snapsToBottom = (mode: FabMode): boolean =>
	mode === "horizontal-tab";

// Keeps `size` pixels of the launcher inside `extent` with a gutter on both
// ends. A viewport smaller than the launcher pins it to the leading gutter.
export const clampAlong = (
	value: number,
	size: number,
	extent: number,
): number => {
	const max = extent - size - VIEWPORT_GUTTER_PX;
	return Math.min(
		Math.max(value, VIEWPORT_GUTTER_PX),
		Math.max(max, VIEWPORT_GUTTER_PX),
	);
};

export const nearestSide = (box: Box, viewport: Viewport): FabPosition =>
	box.left + box.width / 2 < viewport.width / 2 ? "left" : "right";

/**
 * Where a launcher released at `box` comes to rest. `sideInsetPx` is the
 * configured margin from the side edge; the vertical tab is flush with it.
 */
export const resolveRestingPlace = (
	mode: FabMode,
	box: Box,
	viewport: Viewport,
	sideInsetPx: number,
): RestingPlace => {
	const side = nearestSide(box, viewport);
	if (snapsToBottom(mode)) {
		return {
			left: clampAlong(box.left, box.width, viewport.width),
			side,
			top: viewport.height - box.height,
		};
	}
	return {
		left:
			side === "left" ? sideInsetPx : viewport.width - box.width - sideInsetPx,
		side,
		top: clampAlong(box.top, box.height, viewport.height),
	};
};

/** The resting place a remembered position maps to in the current viewport. */
export const restingPlaceFromStored = (
	stored: StoredFabPosition,
	size: Pick<Box, "height" | "width">,
	viewport: Viewport,
	sideInsetPx: number,
): RestingPlace => {
	const box: Box = snapsToBottom(stored.mode)
		? { ...size, left: stored.offset, top: viewport.height - size.height }
		: {
				...size,
				left: stored.side === "left" ? 0 : viewport.width - size.width,
				top: stored.offset,
			};
	return resolveRestingPlace(stored.mode, box, viewport, sideInsetPx);
};

export const toStoredPosition = (
	mode: FabMode,
	place: RestingPlace,
): StoredFabPosition => ({
	mode,
	offset: snapsToBottom(mode) ? place.left : place.top,
	side: place.side,
});

/**
 * Where a parked launcher rests after the viewport changed size. It stays on
 * the edge it was parked on – the side is carried over from `place`, not
 * measured again, so the result is right even while the launcher is hidden.
 */
export const reflowRestingPlace = (
	mode: FabMode,
	place: RestingPlace,
	size: Pick<Box, "height" | "width">,
	viewport: Viewport,
	sideInsetPx: number,
): RestingPlace =>
	restingPlaceFromStored(
		toStoredPosition(mode, place),
		size,
		viewport,
		sideInsetPx,
	);

/**
 * Parses a remembered position. Anything malformed, or saved by another
 * launcher mode (a bubble's top means nothing to a horizontal tab), is
 * discarded so the launcher starts from its configured corner.
 */
export const parseStoredPosition = (
	raw: string | null,
	mode: FabMode,
): StoredFabPosition | null => {
	if (raw === null) {
		return null;
	}
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return null;
	}
	if (typeof parsed !== "object" || parsed === null) {
		return null;
	}
	// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- narrowing parsed sessionStorage JSON after the typeof === "object" guard
	const record = parsed as Readonly<Record<string, unknown>>;
	const { offset, side } = record;
	if (
		record["mode"] !== mode ||
		(side !== "left" && side !== "right") ||
		typeof offset !== "number" ||
		!Number.isFinite(offset)
	) {
		return null;
	}
	return { mode, offset, side };
};
